import React, { useState, useEffect, useRef, ReactNode } from 'react';
import { formatToISOString } from '../utils/dateUtils';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    useTheme,
    useMediaQuery,
    Card,
    CardContent,
    Stack,
    Box,
    Typography,
    IconButton,
    Snackbar,
    Alert,
    CircularProgress,
    SelectChangeEvent,
} from '@mui/material';
import { format, set } from 'date-fns';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    ArrowUpward,
    ArrowDownward,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { transactions, wallets, categories } from '../services/api';

const formatDisplayDateTime = (dateString: string) => {
  // 去掉 Z，防止 JS 做 UTC → Local 转换
  const noTimezone = dateString.replace("Z", "");

  return format(new Date(noTimezone), "d/MM/yyyy hh:mm a");
};


interface Transaction {
    record_id: number;
    type: 'cash_in' | 'cash_out';
    amount: number;
    describe: string;
    trans_date: string;
    category_id: number;
    wallets_id: number;
    category_name?: string;
    wallet_name?: string;
}

interface Wallet {
    wallets_id: number;
    name: string;
}

interface Category {
    category_id: number;
    name: string;
}

interface FormData {
    type: 'cash_in' | 'cash_out';
    amount: string;
    describe: string;
    trans_date: string;
    category_id: string;
    wallets_id: string;
}

const TransactionCard: React.FC<{
    transaction: Transaction;
    onEdit: () => void;
    onDelete: () => void;
}> = ({ transaction, onEdit, onDelete }) => {
    return (
        <Card
            sx={{
                mb: 2,
                position: 'relative',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:active': {
                    transform: 'scale(0.98)',
                },
                bgcolor: transaction.type === 'cash_in'
                    ? 'rgba(151, 188, 152, 0.1)'
                    : 'rgba(244, 67, 54, 0.1)',
                color: 'white',
                overflow: 'visible',
                borderRadius: '13px',
            }}
            elevation={3}
        >
            <CardContent sx={{ pb: '16px !important' }}>
                <Stack spacing={1.5}>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        position: 'relative'
                    }}>
                        <Typography variant="subtitle1" component="div" sx={{ color: 'black', fontWeight: 'bold' }}>
                            {formatDisplayDateTime(transaction.trans_date)}
                        </Typography>
                        <Box
                            sx={{
                                backgroundColor: transaction.type === 'cash_in' ? '#8ba88cff' : '#f44336',
                                color: 'white',
                                borderRadius: '16px',
                                padding: '4px 12px',
                                fontSize: '0.875rem'
                            }}
                        >
                            {transaction.type === 'cash_in' ? 'Deposit' : 'Withdraw'}
                        </Box>
                    </Box>
                    <Typography variant="h6" sx={{ color: transaction.type === 'cash_in' ? 'success.main' : 'error.main' }}>
                        RM {transaction.amount}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {transaction.describe}
                    </Typography>
                    <Typography variant="body2" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, backgroundColor: '#ffffff', color: 'black', fontWeight: 'bold', borderRadius: '12px', padding: '4px 8px' }}>
                        {transaction.category_name}
                    </Typography>
                    <Typography variant="body2" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, backgroundColor: '#ffffff', color: 'black', fontWeight: 'bold', borderRadius: '10px', }}>
                        <IconButton color="primary" size="small">
                            <AccountBalanceWalletIcon fontSize="small" />
                        </IconButton>
                        {transaction.wallet_name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={onEdit}
                        >
                            Edit
                        </Button>
                        <Button
                            size="small"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={onDelete}
                        >
                            Delete
                        </Button>
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
};

type SortField = 'date' | 'amount' | 'type';
type SortOrder = 'asc' | 'desc';
type FilterMode = 'all' | 'month' | 'day' | 'year' | 'range' | 'custom';

export const TransactionList = () => {

    const [loading, setLoading] = useState(false);
    // Create ref for pull to refresh
    const listRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    let startY = 0;
    let currentY = 0;
    let lastScrollY = 0;

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [isButtonVisible, setIsButtonVisible] = useState(true);

    // State declarations
    // State declarations
    const [transactionList, setTransactionList] = useState<Transaction[]>([]);
    const [walletList, setWalletList] = useState<Wallet[]>([]);
    const [categoryList, setCategoryList] = useState<Category[]>([]);
    const [sortField, setSortField] = useState<SortField>('date');
    const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [open, setOpen] = useState(false);
    const [editingTransaction, setEditingTransaction] = useState<Transaction | null>(null);
    const [formData, setFormData] = useState<FormData>({
        type: 'cash_out',
        amount: '',
        describe: '',
        trans_date: new Date().toISOString().slice(0, 16) + ':00Z',
        category_id: '',
        wallets_id: '',
    });
    const [walletFilter, setWalletFilter] = useState<string>('all');
    // Advanced filtering
    // default to current month
    const now = new Date();
    const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const defaultYear = `${now.getFullYear()}`;
    const [filterMode, setFilterMode] = useState<FilterMode>('month');
    const [filterMonth, setFilterMonth] = useState<string>(defaultMonth); // YYYY-MM
    const [filterDay, setFilterDay] = useState<string>(''); // YYYY-MM-DD
    const [filterYear, setFilterYear] = useState<string>(defaultYear);
    const [rangeStart, setRangeStart] = useState<string>(''); // YYYY-MM-DD
    const [rangeEnd, setRangeEnd] = useState<string>(''); // YYYY-MM-DD
    const [searchText, setSearchText] = useState<string>(''); // free text search
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error';
    }>({
        open: false,
        message: '',
        severity: 'success'
    });

    const applyFilters = (transactionsToFilter: Transaction[]) => {
        let res = [...transactionsToFilter];

        // Wallet filter (keeps previous behaviour)
        if (walletFilter !== 'all') {
            const walletId = parseInt(walletFilter);
            if (!Number.isNaN(walletId)) {
                res = res.filter(t => t.wallets_id === walletId);
            }
        }

        // Mode-specific filters
        if (filterMode === 'month' && filterMonth) {
            // filterMonth format: YYYY-MM
            res = res.filter(t => {
                const d = new Date(t.trans_date);
                const yy = d.getFullYear();
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                return `${yy}-${mm}` === filterMonth;
            });
        } else if (filterMode === 'day' && filterDay) {
            // filterDay format: YYYY-MM-DD
            res = res.filter(t => t.trans_date.slice(0, 10) === filterDay);
        } else if (filterMode === 'year' && filterYear) {
            // filterYear format: YYYY
            res = res.filter(t => t.trans_date.slice(0, 4) === filterYear);
        } else if (filterMode === 'range' && rangeStart && rangeEnd) {
            const start = new Date(rangeStart).getTime();
            // include whole end day by adding 23:59:59
            const end = new Date(rangeEnd).getTime() + (24 * 60 * 60 * 1000 - 1);
            res = res.filter(t => {
                const tm = new Date(t.trans_date).getTime();
                return tm >= start && tm <= end;
            });
        }

        // Free-text search: category, wallet or description
        if (searchText.trim()) {
            const q = searchText.toLowerCase();
            res = res.filter(t => (
                (t.describe || '').toLowerCase().includes(q) ||
                (t.category_name || '').toLowerCase().includes(q) ||
                (t.wallet_name || '').toLowerCase().includes(q)
            ));
        }

        return res;
    };

    const filteredTransactions = applyFilters(transactionList);

    const calculateSummary = () => {
        const income = filteredTransactions
            .filter(t => t.type === 'cash_in')
            .reduce((sum, t) => sum + Number(t.amount), 0);

        const expenses = filteredTransactions
            .filter(t => t.type === 'cash_out')
            .reduce((sum, t) => sum + Number(t.amount), 0);

        const balance = income - expenses;

        // 找到当前选中的钱包
        const selectedWallet = walletList.find(
            w => walletFilter !== 'all' && w.wallets_id === parseInt(walletFilter)
        );

        // 如果有选中的钱包，就用该钱包的余额，否则取所有钱包余额总和
        const walletBalance = selectedWallet
            ? Number(0)
            : walletList.reduce((sum, w) => sum + Number(0), 0);

        return {
            income: Number(income.toFixed(2)),
            expenses: Number(expenses.toFixed(2)),
            balance: Number(balance.toFixed(2)),
            walletBalance: Number(walletBalance.toFixed(2))
        };
    };

    const summary = calculateSummary();

    const getLocalDateTimeString = () => {
        const now = new Date();
        now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
        return now.toISOString().slice(0, 16);
    };

    // Using the top-level formatDisplayDateTime function

    useEffect(() => {
        loadTransactions();
        loadWallets();
        loadCategories();
    }, []);

    useEffect(() => {
        setTransactionList(prev => sortTransactions([...prev]));
    }, [sortField, sortOrder]);

    // Pull to refresh functionality for mobile
    useEffect(() => {
        if (!isMobile || !listRef.current) return;

        const handleTouchStart = (e: TouchEvent) => {
            startY = e.touches[0].pageY;
        };

        const handleTouchMove = (e: TouchEvent) => {
            currentY = e.touches[0].pageY;
            const pull = currentY - startY;

            if (pull > 0 && window.scrollY === 0) {
                e.preventDefault();
                listRef.current!.style.transform = `translateY(${Math.min(pull / 2, 100)}px)`;
            }
        };

        const handleTouchEnd = () => {
            if (!listRef.current) return;

            const pull = currentY - startY;
            listRef.current.style.transition = 'transform 0.3s ease-out';
            listRef.current.style.transform = 'translateY(0)';

            if (pull > 100) {
                loadTransactions();
            }

            setTimeout(() => {
                if (listRef.current) {
                    listRef.current.style.transition = '';
                }
            }, 300);
        };

        const element = listRef.current;
        element.addEventListener('touchstart', handleTouchStart);
        element.addEventListener('touchmove', handleTouchMove, { passive: false });
        element.addEventListener('touchend', handleTouchEnd);

        return () => {
            element.removeEventListener('touchstart', handleTouchStart);
            element.removeEventListener('touchmove', handleTouchMove);
            element.removeEventListener('touchend', handleTouchEnd);
        };
    }, [isMobile]);

    // Scroll event for button hide/show
    useEffect(() => {
        if (!isMobile) return;

        const handleScroll = () => {
            const currentScrollY = window.scrollY;
            
            if (currentScrollY > lastScrollY && currentScrollY > 100) {
                // Scrolling down
                setIsButtonVisible(false);
            } else {
                // Scrolling up
                setIsButtonVisible(true);
            }
            
            lastScrollY = currentScrollY;
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [isMobile]);

    const sortTransactions = (transactions: Transaction[]) => {
        return [...transactions].sort((a, b) => {
            if (sortField === 'date') {
                return sortOrder === 'asc'
                    ? new Date(a.trans_date).getTime() - new Date(b.trans_date).getTime()
                    : new Date(b.trans_date).getTime() - new Date(a.trans_date).getTime();
            }
            if (sortField === 'amount') {
                return sortOrder === 'asc'
                    ? a.amount - b.amount
                    : b.amount - a.amount;
            }
            if (sortField === 'type') {
                return sortOrder === 'asc'
                    ? a.type.localeCompare(b.type)
                    : b.type.localeCompare(a.type);
            }
            return 0;
        });
    };

    const loadTransactions = async () => {
        try {
            setIsRefreshing(true);
            const response = await transactions.getAll();
            const sortedTransactions = sortTransactions(response.data);
            setTransactionList(sortedTransactions);
        } catch (err: any) {
            console.error('Failed to load transactions:', err);
            setSnackbar({
                open: true,
                message: 'Failed to load transactions: ' + (err.message || 'Unknown error'),
                severity: 'error'
            });
        } finally {
            setIsRefreshing(false);
        }
    };

    const loadWallets = async () => {
        try {
            const response = await wallets.getAll();
            setWalletList(response.data);
        } catch (error) {
            console.error('Failed to load wallets:', error);
        }
    };

    const loadCategories = async () => {
        try {
            const response = await categories.getAll();
            setCategoryList(response.data);
        } catch (error) {
            console.error('Failed to load categories:', error);
        }
    };

    const handleOpen = (transaction?: Transaction) => {
        if (transaction) {
            setEditingTransaction(transaction);
            
            const d = new Date(transaction.trans_date);

            setFormData({
                type: transaction.type,
                amount: transaction.amount.toString(),
                describe: transaction.describe || '',
                trans_date: d.toISOString().slice(0, 16) + ':00Z', 
                category_id: transaction.category_id.toString(),
                wallets_id: transaction.wallets_id.toString(),
            });
        } else {
            setEditingTransaction(null);
            setFormData({
                type: 'cash_out',
                amount: '',
                describe: '',
                trans_date: getLocalDateTimeString() + ':00Z',
                category_id: '',
                wallets_id: '',
            });
        }
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setEditingTransaction(null);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (name === 'trans_date' && value) {
            setFormData(prev => ({
                ...prev,
                trans_date: `${value}:00Z`
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);
            const data = {
                ...formData,
                amount: parseFloat(formData.amount),
                category_id: parseInt(formData.category_id),
                wallets_id: parseInt(formData.wallets_id),
            };

            if (editingTransaction) {
                await transactions.update(editingTransaction.record_id, data);
                setSnackbar({
                    open: true,
                    message: 'Transaction updated successfully',
                    severity: 'success'
                });
            } else {
                await transactions.create(data);
                setSnackbar({
                    open: true,
                    message: 'Transaction added successfully',
                    severity: 'success'
                });
            }
            handleClose();
            loadTransactions();
        } catch (error) {
            console.error('Failed to save transaction:', error);
            setSnackbar({
                open: true,
                message: 'Failed to save transaction',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: number) => {
        if (window.confirm('Are you sure you want to delete this transaction?')) {
            try {
                await transactions.delete(id);
                loadTransactions();
                setSnackbar({
                    open: true,
                    message: 'Transaction deleted successfully',
                    severity: 'success'
                });
            } catch (error) {
                console.error('Failed to delete transaction:', error);
                setSnackbar({
                    open: true,
                    message: 'Failed to delete transaction',
                    severity: 'error'
                });
            }
        }
    };

    return (
        <>
            <Box sx={{ mb: 3 }}>
                {!isMobile && (
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2
                    }}>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpen()}
                            sx={{ borderRadius: '13px', textTransform: 'none' }}
                        >
                            Add Transaction
                        </Button>
                    </Box>
                )}

                {/* Summary Card */}
                <Card sx={{ mb: 2, borderRadius: '13px' }}>
                    <CardContent>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 2
                        }}>
                            <Box>
                                <Typography variant="subtitle2">Income</Typography>
                                <Typography variant="h6" color="success.main">
                                    {summary.income.toFixed(2)}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2">Expenses</Typography>
                                <Typography variant="h6" color="error.main">
                                    {summary.expenses.toFixed(2)}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2">Balance</Typography>
                                <Typography variant="h6">
                                    {summary.balance.toFixed(2)}
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                <Box sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'center',
                    flexWrap: 'wrap'
                }}>
                    {/* Wallet Filter */}
                    <FormControl size="small" sx={{
                        minWidth: 120,
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '13px',
                        }
                    }}>
                        <InputLabel>Wallet</InputLabel>
                        <Select
                            value={walletFilter}
                            label="Wallet"
                            onChange={(e) => setWalletFilter(e.target.value)}
                        >
                            <MenuItem value="all">All</MenuItem>
                            {walletList.map(wallet => (
                                <MenuItem key={wallet.wallets_id} value={wallet.wallets_id.toString()}>
                                    {wallet.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {/* Filter mode controls */}
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Filter</InputLabel>
                        <Select
                            value={filterMode}
                            label="Filter"
                            onChange={(e) => setFilterMode(e.target.value as FilterMode)}
                        >
                            <MenuItem value="all">All</MenuItem>
                            <MenuItem value="month">By Month</MenuItem>
                            <MenuItem value="day">By Day</MenuItem>
                            <MenuItem value="year">By Year</MenuItem>
                            <MenuItem value="range">Range</MenuItem>
                            <MenuItem value="custom">Custom</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Conditional inputs for filter values */}
                    {filterMode === 'month' && (
                        <TextField
                            size="small"
                            type="month"
                            value={filterMonth}
                            onChange={(e) => setFilterMonth(e.target.value)}
                            sx={{ width: 160 }}
                        />
                    )}
                    {filterMode === 'year' && (
                        <TextField
                            size="small"
                            type="number"
                            value={filterYear}
                            onChange={(e) => setFilterYear(e.target.value)}
                            sx={{ width: 120 }}
                            inputProps={{ min: 1900, max: 2100 }}
                        />
                    )}
                    {filterMode === 'day' && (
                        <TextField
                            size="small"
                            type="date"
                            value={filterDay}
                            onChange={(e) => setFilterDay(e.target.value)}
                            sx={{ width: 160 }}
                        />
                    )}
                    {filterMode === 'range' && (
                        <>
                            <TextField
                                size="small"
                                type="date"
                                value={rangeStart}
                                onChange={(e) => setRangeStart(e.target.value)}
                                sx={{ width: 140 }}
                            />
                            <TextField
                                size="small"
                                type="date"
                                value={rangeEnd}
                                onChange={(e) => setRangeEnd(e.target.value)}
                                sx={{ width: 140 }}
                            />
                        </>
                    )}
                    {/* Free text search */}
                    <TextField
                        size="small"
                        placeholder="Search ..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        sx={{ minWidth: 220 }}
                    />
                    <FormControl size="small" sx={{
                        minWidth: 120,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '13px',
                        }
                    }}>
                        <InputLabel>Sort by</InputLabel>
                        <Select
                            value={sortField}
                            label="Sort by"
                            onChange={(e) => {
                                setSortField(e.target.value as SortField);
                                setTransactionList(prev => sortTransactions(prev));
                            }}
                        >
                            <MenuItem value="date">Date</MenuItem>
                            <MenuItem value="amount">Amount</MenuItem>
                            <MenuItem value="type">Type</MenuItem>
                        </Select>
                    </FormControl>
                    <IconButton
                        onClick={() => {
                            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
                            setTransactionList(prev => sortTransactions(prev));
                        }}
                        color="primary"
                    >
                        {sortOrder === 'asc' ? <ArrowUpward /> : <ArrowDownward />}
                    </IconButton>
                    {isMobile && (
                        <IconButton
                            onClick={loadTransactions}
                            // loading={loading}
                            color="primary"
                            sx={{
                                animation: isRefreshing
                                    ? 'spin 1s linear infinite'
                                    : 'none',
                                '@keyframes spin': {
                                    '0%': { transform: 'rotate(0deg)' },
                                    '100%': { transform: 'rotate(360deg)' }
                                }
                            }}
                        >
                            <RefreshIcon />
                        </IconButton>
                    )}
                </Box>
            </Box>

            {isMobile ? (
                <Box
                    ref={listRef}
                    sx={{
                        minHeight: '100vh',
                        transition: 'transform 0.2s',
                        pb: 12
                    }}
                >
                    <Stack spacing={2}>
                        {isRefreshing && (
                            <Box sx={{ textAlign: 'center', py: 2 }}>
                                <CircularProgress size={20} />
                            </Box>
                        )}
                        {filteredTransactions.map((transaction) => (
                            <TransactionCard
                                key={transaction.record_id}
                                transaction={transaction}
                                onEdit={() => handleOpen(transaction)}
                                onDelete={() => handleDelete(transaction.record_id)}
                            />
                        ))}
                    </Stack>
                    <Button
                        ref={buttonRef}
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpen()}
                        fullWidth
                        sx={{
                            borderRadius: '13px',
                            textTransform: 'none',
                            position: 'fixed',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            margin: '15px 20px',
                            width: 'calc(100% - 40px)',
                            zIndex: 1000,
                            transform: isButtonVisible ? 'translateY(0)' : 'translateY(120px)',
                            transition: 'transform 0.3s ease-in-out'
                        }}
                    >
                        Add Transaction
                    </Button>
                </Box>
            ) : (
                <TableContainer
                    component={Paper}
                    sx={{
                        maxHeight: { sm: 440 },
                        overflow: 'auto',
                        borderRadius: '13px',
                    }}
                >
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#f5f5f5',
                                }}>Date</TableCell>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#f5f5f5',
                                }}>Type</TableCell>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#f5f5f5',
                                }}>Amount</TableCell>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#f5f5f5',
                                }}>Description</TableCell>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#f5f5f5',
                                }}>Category</TableCell>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#f5f5f5',
                                }}>Wallet</TableCell>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#f5f5f5',
                                }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredTransactions.map((transaction) => (
                                <TableRow
                                    key={transaction.record_id}
                                    sx={{
                                        backgroundColor: transaction.type === 'cash_in'
                                            ? 'rgba(151, 188, 152, 0.1)' // Light green
                                            : 'rgba(244, 67, 54, 0.1)' // Light red
                                    }}
                                >
                                    <TableCell>
                                        {formatDisplayDateTime(transaction.trans_date)}
                                    </TableCell>
                                    <TableCell>
                                        <Box
                                            sx={{
                                                backgroundColor: transaction.type === 'cash_in' ? '#8ba88cff' : '#f44336',
                                                color: 'white',
                                                borderRadius: '16px',
                                                padding: '4px 12px',
                                                display: 'inline-block',
                                                fontWeight: 'bold',
                                                fontSize: '0.875rem'
                                            }}
                                        >
                                            {transaction.type === 'cash_in' ? 'Deposit' : 'Withdraw'}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        RM {transaction.amount}
                                    </TableCell>
                                    <TableCell>{transaction.describe}</TableCell>
                                    <TableCell>{transaction.category_name}</TableCell>
                                    <TableCell>{transaction.wallet_name}</TableCell>
                                    <TableCell>
                                        <IconButton
                                            onClick={() => handleOpen(transaction)}
                                            size="small"
                                            sx={{ mr: 1 }}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            onClick={() => handleDelete(transaction.record_id)}
                                            size="small"
                                            color="error"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>
                    {editingTransaction ? 'Edit Transaction' : 'Add New Transaction'}
                </DialogTitle>
                <DialogContent>
                    {/* Type Selection */}
                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Type</Typography>
                    <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
                        {['cash_in', 'cash_out'].map((t) => (
                            <Button
                                key={t}
                                variant={formData.type === t ? 'contained' : 'outlined'}
                                color={formData.type === t ? 'primary' : 'inherit'}
                                onClick={() => setFormData({ ...formData, type: t as 'cash_in' | 'cash_out' })}
                                sx={{ flexShrink: 0, borderRadius: '13px' }}
                            >
                                {t === 'cash_in' ? 'Cash In' : 'Cash Out'}
                            </Button>
                        ))}
                    </Box>

                    <TextField
                        margin="dense"
                        label="Amount"
                        type="number"
                        fullWidth
                        name="amount"
                        value={formData.amount}
                        onChange={handleChange}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '13px' } }}
                        required
                    />

                    <TextField
                        margin="dense"
                        label="Description"
                        fullWidth
                        name="describe"
                        value={formData.describe}
                        onChange={handleChange}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '13px' } }}
                    />

                    <TextField
                        margin="dense"
                        label="Date & Time"
                        type="datetime-local"
                        fullWidth
                        name="trans_date"
                        value={formData.trans_date ? formData.trans_date.slice(0, 16) : ''}
                        onChange={handleChange}
                        helperText="Date and time of transaction"
                        InputLabelProps={{
                            shrink: true,
                        }}
                        InputProps={{
                            inputProps: {
                                step: 60 // minute steps
                            }
                        }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '13px' } }}
                    />


                    {/* Category Selection */}
                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Category</Typography>
                    <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
                        {categoryList.map((category) => (
                            <Button
                                key={category.category_id}
                                variant={formData.category_id === String(category.category_id) ? 'contained' : 'outlined'}
                                color={formData.category_id === String(category.category_id) ? 'primary' : 'inherit'}
                                onClick={() => setFormData({ ...formData, category_id: String(category.category_id) })}
                                sx={{ flexShrink: 0, borderRadius: '13px' }}
                            >
                                {category.name}
                            </Button>
                        ))}
                    </Box>

                    {/* Wallet Selection */}
                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Wallet</Typography>
                    <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
                        {walletList.map((wallet) => (
                            <Button
                                key={wallet.wallets_id}
                                variant={formData.wallets_id === String(wallet.wallets_id) ? 'contained' : 'outlined'}
                                color={formData.wallets_id === String(wallet.wallets_id) ? 'primary' : 'inherit'}
                                onClick={() => setFormData({ ...formData, wallets_id: String(wallet.wallets_id) })}
                                sx={{ flexShrink: 0, borderRadius: '13px' }}
                            >
                                {wallet.name}
                            </Button>
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} sx={{ borderRadius: '13px' }}>Cancel</Button>
                    <Button onClick={handleSubmit} 
                    variant="contained"
                    sx={{ borderRadius: '13px', width: '60%' }} 
                    loading={loading}
                    >
                        {editingTransaction ? 'Save' : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};
